import React, { ChangeEvent, useEffect, useState } from 'react';
import { makeStyles } from 'tss-react/mui';
import { observer } from 'mobx-react';
import { Grid } from '@mui/material';
import {
  AcceptedAmico,
  Add,
  BasicModal,
  BasicTable,
  CardComponent,
  Chip,
  EmptyAmico1,
  InProgressAmico1,
  MitigatedAmico,
  NoComplianceAssurances,
  PageTitleAndSubTitle,
  PlannedAmico,
} from '@HomaHealth/ui-component-library/lib/_AIQ-components';
import Snackbar from '../../_components/atoms/Snackbar';
import { pageScrollDown } from '../SummaryPage';
import { AlertColor as MuiColor } from '@mui/material/Alert';
import { CircularLoader, Typography } from '@HomaHealth/ui-component-library';
import { useStores } from '../../stores';
import properties from '../../core-utils/properties';
import axios from 'axios';
import {
  cellStyles1,
  Columns,
} from '@HomaHealth/ui-component-library/lib/_AIQ-components/atoms/BasicTable';
import { patientAccessTheme } from '@HomaHealth/ui-component-library/lib/theme/theme';
import TabSelection from '../../_components/molecules/TabSelection';
import RiskDetailsWindow from '../../_components/organisms/RiskDetailsWindow';
import taskBoardConstants from '../../core-utils/messages/TaskBoardConstants';
import AddActivityModal from '../../_components/organisms/AddActivityModal';
import { formatActivityData } from '../../core-utils/helper-utils/riskRegisterHelper';
import { useLocation } from 'react-router-dom';
import FeatureScreenAccess from '../../_components/organisms/FeatureScreenAccess';
import { isFeatureAccessible } from '../../core-utils/helper-utils/userAccess';

export interface TaskCounts {
  active: number;
  suggested: number;
  queued: number;
  completed: number;
  acknowledged: number;
}
interface LocationState {
  riskAsTask?: any; // Replace `any` with the actual type if you have one
}

const TaskBoardPage = observer(() => {
  //Styles
  const useStyles = makeStyles()({
    root: {
      display: 'flex',
      flexDirection: 'column',
      padding: '40px 0px 66px 63px',
      width: '1422px',
    },
    tabs: {
      display: 'flex',
      flexDirection: 'column',
      gap: '25px',
      marginTop: '25px',
    },
  });
  const { classes } = useStyles();
  const location = useLocation();
  const { riskAsTask } = (location.state || {}) as LocationState;

  //Constants
  const [tabList] = useState<string[]>(taskBoardConstants.TABS);
  const TASK_TABLE_COLUMNS: Columns[] = [
    {
      name: 'id',
      cellStyles: cellStyles1,
      align: 'left',
      width: '150px',
    },
    {
      name: 'ID',
      cellStyles: cellStyles1,
      align: 'left',
      width: '150px',
    },
    {
      name: 'Name',
      cellStyles: cellStyles1,
      align: 'left',
      width: '200px',
      showEllipsis: true,
      ellipsisWidth: '200px',
    },
    {
      name: 'Category',
      cellStyles: cellStyles1,
      align: 'left',
      width: '250px',
    },
    {
      name: 'Impact',
      cellStyles: cellStyles1,
      align: 'left',
      width: '300px',
    },
    {
      name: 'Status',
      cellStyles: cellStyles1,
      align: 'left',
      width: '250px',
    },
    {
      name: 'Owner',
      cellStyles: cellStyles1,
      align: 'left',
      width: '250px',
    },
    {
      name: 'Start Date',
      cellStyles: cellStyles1,
      align: 'left',
      width: '250px',
    },
    {
      name: 'Suggested Start Date',
      cellStyles: cellStyles1,
      align: 'left',
      width: '320px',
    },
    {
      name: 'Tracker',
      cellStyles: cellStyles1,
      align: 'left',
      width: '125px',
    },
  ];

  //Stores
  const employerGroupStore = useStores().employerGroupStore;
  const loginPageStore = useStores().loginPageStore;
  const userAccess = loginPageStore.userResponse;
  const combinedScreens = Array.from(
    new Set([
      ...(userAccess.careNavigationScreens || []),
      ...(userAccess.complianceAssuranceScreens || []),
      ...(userAccess.riskManagementScreens || []),
    ]),
  );

  //States
  const [selectedTabIndex, setSelectedTabIndex] = useState<number>(0);
  const [selectedTask, setselectedTask] = useState<any>(null);
  const [TaskCounts, setTaskCounts] = useState<TaskCounts>({
    active: 0,
    suggested: 0,
    queued: 0,
    acknowledged: 0,
    completed: 0,
  });
  const [addTaskModalOpen, setAddTaskModalOpen] = useState<boolean>(false);
  const [taskDetailsWindowOpen, setTaskDetailsWindowOpen] = useState<boolean>(
    false,
  );
  const [selectedTaskId, setSelectedTaskId] = useState<string>('');
  const [deletePopUpOpen, setDeletePopUpOpen] = useState<boolean>(false);

  //Active Task Table States
  const [activeTaskTableloading, setActiveTaskTableLoading] = useState<boolean>(
    false,
  );
  const [activeTaskTableData, setActiveTaskTableData] = useState<any>([]);
  const [
    activeTaskTablePageNumber,
    setActiveTaskTablePageNumber,
  ] = useState<number>(1);
  const [tablePageSize] = useState<number>(5);
  const [
    activeTaskTableTotalPages,
    setActiveTaskTableTotalPages,
  ] = useState<number>(0);

  //Suggested Task Table States
  const [
    suggestedTaskTableloading,
    setSuggestedTaskTableLoading,
  ] = useState<boolean>(false);
  const [suggestedTaskTableData, setSuggestedTaskTableData] = useState<any>([]);
  const [
    suggestedTaskTablePageNumber,
    setSuggestedTaskTablePageNumber,
  ] = useState<number>(1);
  const [
    suggestedTaskTableTotalPages,
    setSuggestedTaskTableTotalPages,
  ] = useState<number>(0);

  //Queued Task Table States
  const [queuedTaskTableloading, setQueuedTaskTableLoading] = useState<boolean>(
    false,
  );
  const [queuedTaskTableData, setQueuedTaskTableData] = useState<any>([]);
  const [
    queuedTaskTablePageNumber,
    setQueuedTaskTablePageNumber,
  ] = useState<number>(1);
  const [
    queuedTaskTableTotalPages,
    setQueuedTaskTableTotalPages,
  ] = useState<number>(0);

  //Completed Tasks Table States
  const [
    completedTaskTableloading,
    setCompletedTaskTableLoading,
  ] = useState<boolean>(false);
  const [completedTaskTableData, setCompletedTaskTableData] = useState<any>([]);
  const [
    completedTaskTablePageNumber,
    setCompletedTaskTablePageNumber,
  ] = useState<number>(1);
  const [
    completedTaskTableTotalPages,
    setCompletedTaskTableTotalPages,
  ] = useState<number>(0);

  //Acknowledged Tasks Table
  const [
    acknowledgedTaskTableloading,
    setAcknowledgedTaskTableLoading,
  ] = useState<boolean>(false);
  const [
    acknowledgedTaskTableData,
    setAcknowledgedTaskTableData,
  ] = useState<any>([]);
  const [
    acknowledgedTaskTablePageNumber,
    setAcknowledgedTaskTablePageNumber,
  ] = useState<number>(1);
  const [
    acknowledgedTaskTableTotalPages,
    setAcknowledgedTaskTableTotalPages,
  ] = useState<number>(0);

  //snackbar
  const handleOpenSnackBar = (
    message: string,
    severity: MuiColor,
    duration: number | null,
  ) => {
    setSnackbarState({
      open: true,
      message: message,
      severity: severity,
      duration: duration,
    });
  };

  const handleCloseSnackBar = (
    event?: React.SyntheticEvent,
    reason?: string,
  ) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbarState({
      open: false,
      message: '',
      severity: undefined,
      duration: null,
    });
  };

  const [snackbarState, setSnackbarState] = React.useState<{
    open: boolean;
    message: string;
    severity: MuiColor | undefined;
    duration: null | number;
  }>({
    open: false,
    message: '',
    severity: undefined,
    duration: null,
  });

  //Functions
  const handleTaskClick = (task: any) => {
    setselectedTask(task);
    setTaskDetailsWindowOpen(true);
  };

  const handleSelectedIndex = (index: number) => {
    setSelectedTabIndex(index);
  };

  const handleMenuItem = (index: number, item: any, event: any, rowId: any) => {
    let taskId = '';
    switch (rowId) {
      case 'activeTasks':
        taskId = activeTaskTableData[index][0];
        break;
      case 'suggestedTasks':
        taskId = suggestedTaskTableData[index][0];
        break;
      case 'queuedTasks':
        taskId = queuedTaskTableData[index][0];
        break;
      case 'completedTasks':
        taskId = completedTaskTableData[index][0];
        break;
      case 'acknowledgedTasks':
        taskId = acknowledgedTaskTableData[index][0];
        break;
      default:
        break;
    }
    switch (item) {
      case 'Delete':
        setSelectedTaskId(taskId);
        setDeletePopUpOpen(true);
        break;
    }
  };

  const getTabsContent = () => {
    switch (tabList[selectedTabIndex]) {
      case 'Active and Suggested Tasks':
        return <ActiveAndSuggestedTasks />;
      case 'Queued for Action':
        return <QueuedTasks />;
      case 'Completed Tasks':
        return <CompletedAndAcknowlgedTasks />;
      default:
        return <></>;
    }
  };

  //Apis

  const handleAddTask = async (data: any) => {
    await axios.post(
      properties.aiqBaseUrl +
        properties.riskRegisterBaseUrl +
        properties.addActivity,
      {
        ...data,
        // eg_nid: 'crane_nxt_1742408999728',
        eg_nid: employerGroupStore.employerGroupResponseData.eg_nid[0],
      },
    );
    getActiveTaskss();
    getSuggestedTasks();
    getQueuedTasks();
    getCompletedTasks();
    getAcknowledgedTasks();
  };

  const deleteTask = async () => {
    handleOpenSnackBar('Task deletion in progress', 'info', 2000);
    await axios
      .post(
        properties.aiqBaseUrl +
          properties.riskRegisterBaseUrl +
          properties.deleteActivity,
        {
          activity_id: selectedTaskId,
        },
      )
      .then((res: any) => {
        if (res.status === 200) {
          handleOpenSnackBar('Task has been deleted', 'success', 2000);
          getActiveTaskss();
          getSuggestedTasks();
          getQueuedTasks();
          getCompletedTasks();
          getAcknowledgedTasks();
        }
      })
      .catch((err: any) => {
        handleOpenSnackBar('Task deletion failed.', 'error', 3000);
        throw err;
      });
  };

  const handleUpdateTask = async (data: any) => {
    const formData = new FormData();
    for (const key in data) {
      if (key === 'files') {
        continue;
      } // handle files separately below

      const value = data[key];

      if (Array.isArray(value)) {
        if (value.length && typeof value[0] === 'object') {
          // For arrays of objects like `activity`
          formData.append(key, JSON.stringify(value));
        } else {
          // For arrays of strings like `impact`, `owner`
          value.forEach((item: any, index: number) => {
            formData.append(`${key}[${index}]`, item);
          });
        }
      } else if (typeof value === 'object' && value !== null) {
        // In case any single object sneaks in
        formData.append(key, JSON.stringify(value));
      } else if (value !== '') {
        formData.append(key, value); // Only append non-empty values
      }
    }
    formData.append(
      'eg_nid',
      // 'crane_nxt_1742408999728',
      employerGroupStore.employerGroupResponseData.eg_nid[0],
    );
    formData.append(
      'employer',
      employerGroupStore.employerGroupResponseData.employer_group_name[0],
    );
    if (data.files && Array.isArray(data.files)) {
      data.files.forEach((file: File) => {
        formData.append('files', file);
      });
    }
    await axios.post(
      properties.aiqBaseUrl +
        properties.riskRegisterBaseUrl +
        properties.updateRisk,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } },
    );
    getActiveTaskss();
    getSuggestedTasks();
    getQueuedTasks();
    getCompletedTasks();
    getAcknowledgedTasks();
  };

  const getTasks = async (status: any, pageNumber: number) => {
    return await axios.post(
      properties.aiqBaseUrl +
        properties.riskRegisterBaseUrl +
        properties.getActivities,
      {
        // eg_nid: 'crane_nxt_1742408999728',
        eg_nid: employerGroupStore.employerGroupResponseData.eg_nid[0],
        page: pageNumber,
        limit: tablePageSize,
        status: status,
      },
    );
  };

  const getActiveTaskss = async () => {
    const tasksData = await getTasks(
      ['In Progress', 'In Review'],
      activeTaskTablePageNumber,
    );
    const tasks = tasksData.data.data.activities;
    const inProgressCount =
      tasksData.data?.data?.totalFlattenedCount['In Progress'] ?? 0;
    const inReviewCount =
      tasksData.data?.data?.totalFlattenedCount['In Review'] ?? 0;
    setTaskCounts((prev) => ({
      ...prev,
      active: inProgressCount + inReviewCount,
    }));
    const formattedNewTasks = formatActivityData(tasks, handleTaskClick);
    setActiveTaskTableData(formattedNewTasks);
    setActiveTaskTableTotalPages(Math.ceil(tasksData.data?.data?.totalPages));
  };

  const getSuggestedTasks = async () => {
    const tasksData = await getTasks(
      ['Suggested'],
      suggestedTaskTablePageNumber,
    );
    const formattedSuggestedTaskTableData = formatActivityData(
      tasksData?.data?.data?.activities,
      handleTaskClick,
    );
    setSuggestedTaskTableData(formattedSuggestedTaskTableData);
    setSuggestedTaskTableTotalPages(
      Math.ceil(tasksData.data?.data?.totalPages),
    );
    setTaskCounts((prev) => ({
      ...prev,
      suggested: tasksData?.data?.data?.totalFlattenedCount['Suggested'] ?? 0,
    }));
  };

  const getQueuedTasks = async () => {
    const tasksData = await getTasks(['Queued'], queuedTaskTablePageNumber);
    const formattedQueuedTaskTableData = formatActivityData(
      tasksData?.data?.data?.activities,
      handleTaskClick,
    );
    setQueuedTaskTableData(formattedQueuedTaskTableData);
    setQueuedTaskTableTotalPages(Math.ceil(tasksData.data?.data?.totalPages));
    setTaskCounts((prev) => ({
      ...prev,
      queued: tasksData?.data?.data?.totalFlattenedCount['Queued'] ?? 0,
    }));
  };

  const getCompletedTasks = async () => {
    const tasksData = await getTasks(
      ['Completed'],
      completedTaskTablePageNumber,
    );
    const formattedCompletedTaskTableData = await formatActivityData(
      tasksData?.data?.data?.activities,
      handleTaskClick,
    );

    setCompletedTaskTableData(formattedCompletedTaskTableData);
    setCompletedTaskTableTotalPages(
      Math.ceil(tasksData.data?.data?.totalPages),
    );
    setTaskCounts((prev) => ({
      ...prev,
      completed: tasksData?.data?.data?.totalFlattenedCount['Completed'] ?? 0,
    }));
  };

  const getAcknowledgedTasks = async () => {
    const tasksData = await getTasks(
      ['Acknowledged'],
      acknowledgedTaskTablePageNumber,
    );
    const formattedAcknowledgedTaskTableData = formatActivityData(
      tasksData?.data?.data?.activities,
      handleTaskClick,
    );
    setAcknowledgedTaskTableData(formattedAcknowledgedTaskTableData);
    setAcknowledgedTaskTableTotalPages(
      Math.ceil(tasksData.data?.data?.totalPages),
    );
    setTaskCounts((prev) => ({
      ...prev,
      acknowledged:
        tasksData?.data?.data?.totalFlattenedCount['Acknowledged'] ?? 0,
    }));
  };

  //UseEffects

  useEffect(() => {
    const fetchTasks = async () => {
      if (!isFeatureAccessible('TASK_BOARD', combinedScreens, userAccess)) {
        return;
      }
      setActiveTaskTableLoading(true);
      await Promise.all([getActiveTaskss()]);
      setActiveTaskTableLoading(false);
    };
    fetchTasks();
  }, [activeTaskTablePageNumber]);

  useEffect(() => {
    const fetchTasks = async () => {
      if (!isFeatureAccessible('TASK_BOARD', combinedScreens, userAccess)) {
        return;
      }
      setSuggestedTaskTableLoading(true);
      await Promise.all([getSuggestedTasks()]);
      setSuggestedTaskTableLoading(false);
    };
    fetchTasks();
  }, [suggestedTaskTablePageNumber]);

  useEffect(() => {
    const fetchTasks = async () => {
      if (!isFeatureAccessible('TASK_BOARD', combinedScreens, userAccess)) {
        return;
      }
      setQueuedTaskTableLoading(true);
      await Promise.all([getQueuedTasks()]);
      setQueuedTaskTableLoading(false);
    };
    fetchTasks();
  }, [queuedTaskTablePageNumber]);

  useEffect(() => {
    const fetchTasks = async () => {
      if (!isFeatureAccessible('TASK_BOARD', combinedScreens, userAccess)) {
        return;
      }
      setCompletedTaskTableLoading(true);
      await Promise.all([getCompletedTasks()]);
      setCompletedTaskTableLoading(false);
    };
    fetchTasks();
  }, [completedTaskTablePageNumber]);

  useEffect(() => {
    const fetchTasks = async () => {
      if (!isFeatureAccessible('TASK_BOARD', combinedScreens, userAccess)) {
        return;
      }
      setAcknowledgedTaskTableLoading(true);
      await Promise.all([getAcknowledgedTasks()]);
      setAcknowledgedTaskTableLoading(false);
    };
    fetchTasks();
  }, [acknowledgedTaskTablePageNumber]);

  useEffect(() => {
    if (riskAsTask) {
      handleTaskClick(riskAsTask);
    }
  }, [riskAsTask]);

  //Components
  const ActiveAndSuggestedTasks = () => {
    return (
      <>
        <CardComponent
          title={taskBoardConstants.TABLE1_TITLE}
          chip={
            <Chip
              height="27px"
              borderRadius="4px"
              padding="5px 10px"
              label={
                <Grid
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '15px',
                  }}
                >
                  <Typography
                    style={{
                      fontSize: '16px',
                      color: patientAccessTheme.palette.text.highemphasis,
                      fontWeight: 600,
                    }}
                  >
                    {TaskCounts?.active}
                  </Typography>
                  <Typography
                    style={{
                      fontSize: '12px',
                      color: patientAccessTheme.palette.text.mediumemphasis,
                    }}
                  >
                    Active
                  </Typography>
                </Grid>
              }
              backgroundColor={patientAccessTheme.palette.background.default}
            />
          }
          body={
            <Grid style={{ marginTop: '20px' }}>
              {activeTaskTableloading ? (
                <CircularLoader />
              ) : activeTaskTableData?.length > 0 ? (
                <BasicTable
                  isMenuAvailable={true}
                  handleCellMenuItem={handleMenuItem}
                  menuOptions={taskBoardConstants.MENU_OPTIONS}
                  rowId={'activeTasks'}
                  columns={TASK_TABLE_COLUMNS.filter(
                    (col) => col.name !== 'Suggested Start Date',
                  )}
                  displayType="row"
                  rowsData={activeTaskTableData}
                  hideColumnIndex={0}
                  showPagination={true}
                  pageCount={activeTaskTableTotalPages}
                  pageNumber={activeTaskTablePageNumber}
                  handlePagination={(
                    event: ChangeEvent<unknown>,
                    page: number,
                  ) => {
                    setActiveTaskTablePageNumber(page);
                  }}
                />
              ) : (
                <NoComplianceAssurances
                  isImageOnTop={true}
                  title={taskBoardConstants.NO_ACTIVE_TASKS}
                  subTitle={taskBoardConstants.NO_ACTIVE_TASKS_SUB}
                  image={<InProgressAmico1 />}
                  buttonLabel={taskBoardConstants.ADD_TASK}
                  handleOnclick={() => {
                    setAddTaskModalOpen(true);
                  }}
                  startIcon={<Add />}
                  rootStyles={{
                    border: '0px',
                    background: '',
                    padding: '20px',
                    height: '350px',
                  }}
                  titleFont="18px"
                  subTitleFont="14px"
                  subTitleMargin="0px"
                  buttonWidth="240px"
                />
              )}
            </Grid>
          }
          tooltip={taskBoardConstants.NO_ACTIVE_TASKS_SUB}
        />
        <CardComponent
          title={taskBoardConstants.TABLE2_TITLE}
          chip={
            <Chip
              height="27px"
              borderRadius="4px"
              padding="5px 10px"
              label={
                <Grid
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '15px',
                  }}
                >
                  <Typography
                    style={{
                      fontSize: '16px',
                      color: patientAccessTheme.palette.text.highemphasis,
                      fontWeight: 600,
                    }}
                  >
                    {TaskCounts?.suggested}
                  </Typography>
                  <Typography
                    style={{
                      fontSize: '12px',
                      color: patientAccessTheme.palette.text.mediumemphasis,
                    }}
                  >
                    Suggested
                  </Typography>
                </Grid>
              }
              backgroundColor={patientAccessTheme.palette.background.default}
            />
          }
          body={
            <Grid style={{ marginTop: '20px' }}>
              {suggestedTaskTableloading ? (
                <CircularLoader />
              ) : suggestedTaskTableData?.length > 0 ? (
                <BasicTable
                  isMenuAvailable={true}
                  handleCellMenuItem={handleMenuItem}
                  menuOptions={taskBoardConstants.MENU_OPTIONS}
                  rowId={'suggestedTasks'}
                  displayType="row"
                  columns={TASK_TABLE_COLUMNS.filter(
                    (col) => col.name !== 'Start Date',
                  )}
                  rowsData={suggestedTaskTableData}
                  hideColumnIndex={0}
                  showPagination={true}
                  pageCount={suggestedTaskTableTotalPages}
                  pageNumber={suggestedTaskTablePageNumber}
                  handlePagination={(
                    event: ChangeEvent<unknown>,
                    page: number,
                  ) => {
                    setSuggestedTaskTablePageNumber(page);
                  }}
                />
              ) : (
                <NoComplianceAssurances
                  isImageOnTop={true}
                  title={taskBoardConstants.NO_SUGGESTED_TASKS}
                  subTitle={taskBoardConstants.NO_SUGGESTED_TASKS_SUB}
                  image={<EmptyAmico1 />}
                  buttonLabel={taskBoardConstants.ADD_TASK}
                  handleOnclick={() => {
                    setAddTaskModalOpen(true);
                  }}
                  startIcon={<Add />}
                  rootStyles={{
                    border: '0px',
                    background: '',
                    padding: '20px',
                    height: '350px',
                  }}
                  titleFont="18px"
                  subTitleFont="14px"
                  subTitleMargin="0px"
                  buttonWidth="240px"
                />
              )}
            </Grid>
          }
          tooltip={taskBoardConstants.NO_SUGGESTED_TASKS_SUB}
        />
      </>
    );
  };

  const QueuedTasks = () => {
    return (
      <CardComponent
        title={taskBoardConstants.TABLE3_TITLE}
        chip={
          <Chip
            height="27px"
            borderRadius="4px"
            padding="5px 10px"
            label={
              <Grid
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '15px',
                }}
              >
                <Typography
                  style={{
                    fontSize: '16px',
                    color: patientAccessTheme.palette.text.highemphasis,
                    fontWeight: 600,
                  }}
                >
                  {TaskCounts?.queued}
                </Typography>
                <Typography
                  style={{
                    fontSize: '12px',
                    color: patientAccessTheme.palette.text.mediumemphasis,
                  }}
                >
                  Queued
                </Typography>
              </Grid>
            }
            backgroundColor={patientAccessTheme.palette.background.default}
          />
        }
        body={
          <Grid style={{ marginTop: '20px' }}>
            {queuedTaskTableloading ? (
              <CircularLoader />
            ) : queuedTaskTableData?.length > 0 ? (
              <BasicTable
                isMenuAvailable={true}
                handleCellMenuItem={handleMenuItem}
                menuOptions={taskBoardConstants.MENU_OPTIONS}
                rowId={'queuedTasks'}
                displayType="row"
                columns={TASK_TABLE_COLUMNS.filter(
                  (col) => col.name !== 'Suggested Start Date',
                )}
                rowsData={queuedTaskTableData}
                hideColumnIndex={0}
                showPagination={true}
                pageCount={queuedTaskTableTotalPages}
                pageNumber={queuedTaskTablePageNumber}
                handlePagination={(
                  event: ChangeEvent<unknown>,
                  page: number,
                ) => {
                  setQueuedTaskTablePageNumber(page);
                }}
              />
            ) : (
              <NoComplianceAssurances
                isImageOnTop={true}
                title={taskBoardConstants.NO_QUEUED_TASKS}
                subTitle={taskBoardConstants.NO_QUEUED_TASKS_SUB}
                image={<PlannedAmico />}
                rootStyles={{
                  border: '0px',
                  background: '',
                  padding: '20px',
                  height: '350px',
                }}
                titleFont="18px"
                subTitleFont="14px"
                subTitleMargin="0px"
                handleOnclick={() => {}}
                hideButton={true}
              />
            )}
          </Grid>
        }
        tooltip={taskBoardConstants.NO_QUEUED_TASKS_SUB}
      />
    );
  };

  const CompletedAndAcknowlgedTasks = () => {
    return (
      <>
        <CardComponent
          title={taskBoardConstants.TABLE4_TITLE}
          chip={
            <Chip
              height="27px"
              borderRadius="4px"
              padding="5px 10px"
              label={
                <Grid
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '15px',
                  }}
                >
                  <Typography
                    style={{
                      fontSize: '16px',
                      color: patientAccessTheme.palette.text.highemphasis,
                      fontWeight: 600,
                    }}
                  >
                    {TaskCounts?.completed}
                  </Typography>
                  <Typography
                    style={{
                      fontSize: '12px',
                      color: patientAccessTheme.palette.text.mediumemphasis,
                    }}
                  >
                    Completed
                  </Typography>
                </Grid>
              }
              backgroundColor={patientAccessTheme.palette.background.default}
            />
          }
          body={
            <Grid style={{ marginTop: '20px' }}>
              {completedTaskTableloading ? (
                <CircularLoader />
              ) : completedTaskTableData?.length > 0 ? (
                <BasicTable
                  isMenuAvailable={true}
                  handleCellMenuItem={handleMenuItem}
                  menuOptions={taskBoardConstants.MENU_OPTIONS}
                  rowId={'completedTasks'}
                  displayType="row"
                  columns={TASK_TABLE_COLUMNS.filter(
                    (col) => col.name !== 'Suggested Start Date',
                  )}
                  rowsData={completedTaskTableData}
                  hideColumnIndex={0}
                  showPagination={true}
                  pageCount={completedTaskTableTotalPages}
                  pageNumber={completedTaskTablePageNumber}
                  handlePagination={(
                    event: ChangeEvent<unknown>,
                    page: number,
                  ) => {
                    setCompletedTaskTablePageNumber(page);
                  }}
                />
              ) : (
                <NoComplianceAssurances
                  isImageOnTop={true}
                  title={taskBoardConstants.NO_COMPLETED_TASKS}
                  subTitle={taskBoardConstants.NO_COMPLETED_TASKS_SUB}
                  image={<MitigatedAmico />}
                  handleOnclick={() => {}}
                  rootStyles={{
                    border: '0px',
                    background: '',
                    padding: '20px',
                    height: '350px',
                  }}
                  titleFont="18px"
                  subTitleFont="14px"
                  subTitleMargin="0px"
                  hideButton={true}
                />
              )}
            </Grid>
          }
          tooltip={taskBoardConstants.NO_COMPLETED_TASKS_SUB}
        />
        <CardComponent
          title={taskBoardConstants.TABLE5_TITLE}
          chip={
            <Chip
              height="27px"
              borderRadius="4px"
              padding="5px 10px"
              label={
                <Grid
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '15px',
                  }}
                >
                  <Typography
                    style={{
                      fontSize: '16px',
                      color: patientAccessTheme.palette.text.highemphasis,
                      fontWeight: 600,
                    }}
                  >
                    {TaskCounts?.acknowledged}
                  </Typography>
                  <Typography
                    style={{
                      fontSize: '12px',
                      color: patientAccessTheme.palette.text.mediumemphasis,
                    }}
                  >
                    Acknowledged
                  </Typography>
                </Grid>
              }
              backgroundColor={patientAccessTheme.palette.background.default}
            />
          }
          body={
            <Grid style={{ marginTop: '20px' }}>
              {acknowledgedTaskTableloading ? (
                <CircularLoader />
              ) : acknowledgedTaskTableData?.length > 0 ? (
                <BasicTable
                  isMenuAvailable={true}
                  menuOptions={taskBoardConstants.MENU_OPTIONS}
                  handleCellMenuItem={handleMenuItem}
                  rowId={'acknowledgedTasks'}
                  displayType="row"
                  columns={TASK_TABLE_COLUMNS.filter(
                    (col) => col.name !== 'Suggested Start Date',
                  )}
                  rowsData={acknowledgedTaskTableData}
                  hideColumnIndex={0}
                  showPagination={true}
                  pageCount={acknowledgedTaskTableTotalPages}
                  pageNumber={acknowledgedTaskTablePageNumber}
                  handlePagination={(
                    event: ChangeEvent<unknown>,
                    page: number,
                  ) => {
                    setAcknowledgedTaskTablePageNumber(page);
                  }}
                />
              ) : (
                <NoComplianceAssurances
                  isImageOnTop={true}
                  title={taskBoardConstants.NO_ACKNOWLEGED_TASKS}
                  subTitle={taskBoardConstants.NO_ACKNOWLEGED_TASKS_SUB}
                  image={<AcceptedAmico />}
                  handleOnclick={() => {}}
                  rootStyles={{
                    border: '0px',
                    background: '',
                    padding: '20px',
                    height: '350px',
                  }}
                  titleFont="18px"
                  subTitleFont="14px"
                  subTitleMargin="0px"
                  hideButton={true}
                />
              )}
            </Grid>
          }
          tooltip={taskBoardConstants.NO_ACKNOWLEGED_TASKS_SUB}
        />
      </>
    );
  };

  return (
    <>
      <div>
        <Grid container className={classes.root}>
          <FeatureScreenAccess
            noAccess={isFeatureAccessible(
              'TASK_BOARD',
              combinedScreens,
              userAccess,
            )}
          >
            <Grid
              style={{
                marginBottom: '36px',
                position: 'relative',
              }}
            >
              <PageTitleAndSubTitle
                title={taskBoardConstants.TITLE}
                subTitle={''}
                subTitleStyles={{ width: '90%' }}
                customButton={true}
                customButtonText={taskBoardConstants.ADD_TASK}
                customButtonVariant={'contained'}
                customButtonStartIcon={<Add style={{ color: 'white' }} />}
                customButtonClick={() => {
                  setAddTaskModalOpen(true);
                }}
                customButtonWidth={'150px'}
              />
            </Grid>
            <TabSelection
              selectedTabIndex={selectedTabIndex}
              setSelectedTabIndex={handleSelectedIndex}
              tabList={tabList}
              margin="0px"
            />
            <Grid className={classes.tabs}>{getTabsContent()}</Grid>
            {taskDetailsWindowOpen && (
              <RiskDetailsWindow
                activity={selectedTask}
                setDetailsWindowOpen={setTaskDetailsWindowOpen}
                handleSave={handleUpdateTask}
                handleOpenSnackBar={handleOpenSnackBar}
                isActivityWindow={true}
              />
            )}
            {addTaskModalOpen && (
              <AddActivityModal
                open={addTaskModalOpen}
                setOpen={setAddTaskModalOpen}
                handleOpenSnackBar={handleOpenSnackBar}
                handleSubmit={handleAddTask}
                // eg_nid={'crane_nxt_1742408999728'}
                eg_nid={employerGroupStore.employerGroupResponseData.eg_nid[0]}
              />
            )}
            {deletePopUpOpen && (
              <BasicModal
                open={deletePopUpOpen}
                title={taskBoardConstants.DELETE}
                body={
                  <Typography
                    fontSize="12px"
                    lineHeight="17px"
                    fontType="medium"
                    textColor={patientAccessTheme.palette.text.highemphasis}
                    margin="0px 0px 15px 0px"
                  >
                    {taskBoardConstants.DELETE_DESC}
                  </Typography>
                }
                cancelButtonLabel={taskBoardConstants.CANCEL}
                confirmButtonLabel={taskBoardConstants.CONFIRM}
                handleCancel={() => setDeletePopUpOpen(false)}
                handleConfirm={() => {
                  deleteTask();
                  setDeletePopUpOpen(false);
                }}
                handleClose={() => setDeletePopUpOpen(false)}
              />
            )}
            <div id="scroll-top-element"></div>
          </FeatureScreenAccess>
        </Grid>
        {pageScrollDown('scroll-top-element')}
      </div>
      <Snackbar
        {...snackbarState}
        handleClose={handleCloseSnackBar}
        vertical="bottom"
        horizontal="right"
      />
    </>
  );
});

export default TaskBoardPage;
